import React, { useState } from 'react';
import { useContext, useEffect } from 'react';
import NotFound from '../../../../../assets/html/notFound';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { CustomTranslationContext } from '../../../../../context/CustomTranslationContext';
import {
  CreateCustomTranslationResponse,
  CreateCustomTranslationVariables,
  CustomTranslationMutations,
} from '../../../../../graphql/translations/customTranslation.graphql';
import { TranslationUtils } from '../../../../../types/translation/common';
import { CustomTranslation } from '../../../../../types/translation/customTranslation';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { englishTranslations, t } from '../../../../../types/translation/Translator';
import Table, { TableHeader } from '../../../../components/Table';
import { TranslationFilter } from '../../../../../VentoryUI/components/filters/TranslationFilter';
import LoadingScreen from '../../../Common/LoadingScreen';
import CustomTranslationRecordModal from './Modals/CustomTranslationRecordModal';
import { SearchFilter } from '../../../../components/Filter';
import useMutation from '../../../../../hooks/useMutation';

export default function DisplaySettingsCustomTranslationsPane() {
  const { customTranslations, customTranslationsLoading, setCustomTranslations } = useContext(CustomTranslationContext);

  const { currentCompany } = useContext(CompanyContext);

  const [createCustomTranslation] = useMutation<CreateCustomTranslationResponse, CreateCustomTranslationVariables>(
    CustomTranslationMutations.create,
    {
      variables: {
        customTranslation: new CustomTranslation({
          companyId: currentCompany.id,
          name: 'Custom Translations',
          translations: '{}',
        }),
      },
      onCompleted: data => {
        setCustomTranslations(
          new Map(customTranslations).set(
            data.createCustomTranslation.id,
            new CustomTranslation(data.createCustomTranslation),
          ),
        );
      },
    },
  );

  useEffect(() => {
    if (!customTranslationsLoading && !customTranslations.size) createCustomTranslation();
  }, []);

  const [items, setItems] = useState(TranslationUtils.allKeys);
  const [props, setProps] = useState<{ open: boolean; key?: TranslationKey }>({ open: false, key: undefined });

  if (customTranslationsLoading) return <LoadingScreen />;
  const customTranslation = [...customTranslations.values()][0];
  if (!customTranslation) return <NotFound />;

  const headers: TableHeader<TranslationKey>[] = [
    {
      key: 'english',
      name: 'English',
      text: (item: TranslationKey) => englishTranslations[item]?.singular.upper || '',
    },
    {
      key: 'custom',
      name: 'Custom',
      text: (item: TranslationKey) => customTranslation.translations[item]?.singular.upper || '',
    },
  ];

  const filter = new TranslationFilter(englishTranslations, customTranslation.translations);

  return (
    <>
      {props.key && props.open ? (
        <CustomTranslationRecordModal
          open={props.open}
          setOpen={open => setProps({ ...props, open })}
          customTranslation={customTranslation}
          translationKey={props.key}
        />
      ) : null}

      <div className='flex flex-grow flex-col gap-8 overflow-hidden'>
        <div className='grid gap-2'>
          <p className='page-title'>{t(TranslationKey.displaySettingsCustomTranslationsTitle).singular.upper}</p>
          <p className='subtitle'>{t(TranslationKey.displaySettingsCustomTranslationsSubtitle).singular.upper}</p>
        </div>
        <div className='flex flex-grow flex-col gap-4'>
          <SearchFilter items={TranslationUtils.allKeys} setItems={setItems} filter={filter} />
          <Table
            filter={filter}
            headers={headers}
            items={items}
            onClick={item => setProps({ open: true, key: item })}
          />
        </div>
      </div>
    </>
  );
}
